/**
 * Recent Orders Panel Component
 * Displays a list of recent orders with status badges
 */

import React from 'react';
import { Order } from '../../types';
import { Badge } from '@/components/ui/badge';

interface RecentOrdersPanelProps {
  orders: Order[];
}

const STATUS_BADGE_STYLES: Record<string, string> = {
  'PENDING': 'bg-yellow-100 text-yellow-800',
  'pending': 'bg-yellow-100 text-yellow-800',
  'CONFIRMED': 'bg-blue-100 text-blue-800',
  'confirmed': 'bg-blue-100 text-blue-800',
  'PROCESSING': 'bg-purple-100 text-purple-800',
  'processing': 'bg-purple-100 text-purple-800',
  'DELIVERED': 'bg-green-100 text-green-800',
  'delivered': 'bg-green-100 text-green-800',
  'CANCELLED': 'bg-red-100 text-red-800',
  'cancelled': 'bg-red-100 text-red-800',
  'IN_PROGRESS': 'bg-indigo-100 text-indigo-800',
  'in_progress': 'bg-indigo-100 text-indigo-800',
  'READY': 'bg-teal-100 text-teal-800',
  'ready': 'bg-teal-100 text-teal-800',
  'DISPATCHED': 'bg-orange-100 text-orange-800',
  'dispatched': 'bg-orange-100 text-orange-800'
};

export const RecentOrdersPanel: React.FC<RecentOrdersPanelProps> = ({ orders }) => {
  if (!orders || orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        No recent orders available
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders.map((order: any) => {
        const statusStyle = STATUS_BADGE_STYLES[order.status] || 'bg-gray-100 text-gray-800';
        const displayStatus = order.status.charAt(0).toUpperCase() + order.status.slice(1).toLowerCase();

        // Parse order date from API response
        const orderDate = order.orderDate instanceof Date
          ? order.orderDate
          : new Date(order.orderDate || new Date());

        // Parse delivery date from API response
        const deliveryDate = order.requiredDeliveryDate instanceof Date
          ? order.requiredDeliveryDate
          : new Date(order.requiredDeliveryDate || order.orderDate || new Date());

        // Get quantity from volumeCubicMeters or totalQuantity
        const quantity = order.volumeCubicMeters || order.totalQuantity || 0;

        // Get final amount, with fallback to totalAmount
        const amount = order.finalAmount || order.totalAmount || 0;

        return (
          <div
            key={order.id}
            className="flex items-start justify-between p-3 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="flex-1 space-y-1">
              {/* Order Number and Status */}
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900 text-sm">{order.orderNumber}</span>
                <Badge className={`text-xs ${statusStyle}`}>
                  {displayStatus}
                </Badge>
              </div>

              {/* Order Details */}
              <div className="text-xs text-gray-600">
                {quantity.toLocaleString()} {order.volumeCubicMeters ? 'cu.m' : 'units'}
              </div>

              {/* Dates */}
              <div className="text-xs text-gray-500 flex gap-4">
                <span>Order: {orderDate.toLocaleDateString('en-IN')}</span>
                <span>Due: {deliveryDate.toLocaleDateString('en-IN')}</span>
              </div>
            </div>

            {/* Amount */}
            <div className="text-right">
              <div className="font-semibold text-gray-900">
                ₹{amount.toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-gray-500">
                {order.status}
              </div>
            </div>
          </div>
        );
      })}

      {/* Summary */}
      <div className="border-t pt-3 mt-3">
        <div className="text-xs text-gray-600 flex justify-between">
          <span>Total: {orders.length} orders</span>
          <span>₹{orders.reduce((sum, o) => sum + (o.finalAmount || o.totalAmount || 0), 0).toLocaleString('en-IN')}</span>
        </div>
      </div>
    </div>
  );
};

export default RecentOrdersPanel;
